import React, { useState } from 'react';
import { FaWhatsapp, FaInstagram, FaFacebookF } from 'react-icons/fa';
import api from '../services/api';
import { usePageMeta } from '../utils/seo';
import { OFFICE } from '../data/escritorio';
import './Contact.css';

const INITIAL_FORM = {
  name: '',
  phone: '',
  email: '',
  message: '',
  preferredContact: 'whatsapp'
};

const formatPhoneMask = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

const Contact = () => {
  usePageMeta({
    title: 'Contato',
    description: 'Fale com a Navarro & Vieira Advogados: Rua Bélgica, 437, Centro, Cambé/PR. Telefone (43) 3035-5730 e plantão 24 horas para urgências criminais.'
  });
  const instagramUrl = OFFICE.instagramUrl;
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: formatPhoneMask(value) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const name = formData.name.trim();
    const phone = formData.phone.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    const email = formData.email.trim();
    const message = formData.message.trim();
    const preferredContact = formData.preferredContact;

    if (!name || !phone || !message) {
      setErrorModal('Preencha todos os campos obrigatórios: nome, telefone e mensagem.');
      return;
    }
    if (phoneDigits.length < 10) {
      setErrorModal('Informe um telefone válido no formato (43) 99999-9999.');
      return;
    }
    if (!preferredContact) {
      setErrorModal('Selecione como prefere ser contatado pela equipe.');
      return;
    }
    if (preferredContact === 'email' && !email) {
      setErrorModal('Para retorno por e-mail, informe um endereço de e-mail válido.');
      return;
    }

    setSending(true);
    setErrorModal('');
    try {
      await api.post('/contact-messages', {
        name,
        phone,
        email,
        message,
        preferredContact
      });
      setFormData(INITIAL_FORM);
      setShowSuccessModal(true);
    } catch (error) {
      if (error.code === 'ERR_NETWORK') {
        setErrorModal('Servidor indisponível no momento. Tente novamente em instantes.');
      } else if (error.response?.status === 404) {
        setErrorModal('Serviço de mensagens ainda não foi publicado no servidor.');
      } else {
        setErrorModal(error.response?.data?.error || 'Não foi possível enviar a mensagem. Tente novamente.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="container">
        <div className="contact-content">
          <section className="contact-main-card contact-centered">
            <div className="contact-info">
              <h2>Canais de atendimento</h2>
              <p>Para solicitar atendimento, fale diretamente por WhatsApp nos números abaixo:</p>

              <div className="contact-phone-grid">
                <article className="contact-phone-card">
                  <h3>Escritório (fixo e WhatsApp)</h3>
                  <p>
                    <a
                      href="https://wa.me/554330355730"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-phone-link"
                    >
                      <FaWhatsapp aria-hidden="true" />
                      <span>(43) 3035-5730</span>
                    </a>
                  </p>
                </article>

                <article className="contact-phone-card">
                  <h3>Dr. Mateus</h3>
                  <p>
                    <a
                      href="https://wa.me/5543988172533"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-phone-link"
                    >
                      <FaWhatsapp aria-hidden="true" />
                      <span>(43) 98817-2533</span>
                    </a>
                  </p>
                </article>

                <article className="contact-phone-card">
                  <h3>Dr. Marcos</h3>
                  <p>
                    <a
                      href="https://wa.me/5543999360457"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="contact-phone-link"
                    >
                      <FaWhatsapp aria-hidden="true" />
                      <span>(43) 99936-0457</span>
                    </a>
                  </p>
                </article>
              </div>

              <h2>Redes sociais oficiais</h2>
              <div className="contact-social-grid">
                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-social-card"
                >
                  <FaInstagram aria-hidden="true" />
                  Instagram
                </a>
                <a
                  href="https://www.facebook.com/navarroevieiraadvogados"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-social-card"
                >
                  <FaFacebookF aria-hidden="true" />
                  Facebook
                </a>
              </div>

              <h2>Horário de funcionamento</h2>
              <p>Segunda a sexta</p>
              <p>Das 09:00 às 11:30 e das 13:30 às 17:30</p>
              <p className="contact-highlight">
                Atendimentos fora desse horário somente para acompanhamento de flagrante e urgências relacionadas à área criminal.
              </p>
            </div>

            <div className="contact-message-section">
              <h2>Envie uma mensagem</h2>
              <form className="contact-message-form" onSubmit={handleSubmit}>
                <input
                  type="text"
                  name="name"
                  placeholder="Nome completo"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="(43) 99999-9999"
                  value={formData.phone}
                  onChange={handleChange}
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={16}
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="E-mail (opcional, obrigatório se preferir contato por e-mail)"
                  value={formData.email}
                  onChange={handleChange}
                />

                <fieldset className="contact-preference-fieldset">
                  <legend>Como prefere ser contatado?</legend>
                  <label className="contact-preference-option">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="whatsapp"
                      checked={formData.preferredContact === 'whatsapp'}
                      onChange={handleChange}
                    />
                    WhatsApp ou ligação
                  </label>
                  <label className="contact-preference-option">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="email"
                      checked={formData.preferredContact === 'email'}
                      onChange={handleChange}
                    />
                    E-mail
                  </label>
                </fieldset>

                <textarea
                  name="message"
                  rows="5"
                  placeholder="Escreva sua mensagem"
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
                <button type="submit" className="btn btn-primary" disabled={sending}>
                  {sending ? 'Enviando...' : 'Enviar mensagem'}
                </button>
              </form>
            </div>

            <div className="contact-location-section">
              <h2>Localização do escritório</h2>
              <div className="contact-map-wrap">
                <iframe
                  title="Localização Navarro & Vieira Advogados"
                  src="https://www.google.com/maps?q=Rua+B%C3%A9lgica,+437,+Centro,+Camb%C3%A9,+PR,+86181-090&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <p className="contact-address">Rua Bélgica, nº 437, Centro, Cambé/PR, CEP: 86181-090</p>
              <a
                href="https://maps.app.goo.gl/dffgJjkMAsMH3XkC7"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                Abrir no Google Maps
              </a>
            </div>
          </section>
        </div>
      </div>

      {showSuccessModal && (
        <div className="contact-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="contact-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
            <h3>Mensagem enviada com sucesso</h3>
            <p>
              Recebemos sua mensagem. Nossa equipe entrará em contato em breve pelo canal que você selecionou.
            </p>
            <p>
              Se o assunto for urgente, entre em contato diretamente com os advogados pelos telefones e WhatsApp
              indicados nesta página.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => setShowSuccessModal(false)}>
              Entendi
            </button>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="contact-modal-overlay" onClick={() => setErrorModal('')}>
          <div className="contact-modal contact-modal-error" onClick={(event) => event.stopPropagation()} role="alertdialog" aria-modal="true">
            <h3>Não foi possível enviar</h3>
            <p>{errorModal}</p>
            <button type="button" className="btn btn-primary" onClick={() => setErrorModal('')}>
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
